import * as React from 'react'

import { getSelectedStore,subscribeSelectedStore } from '@/features/shared'
import type { LocationDetails } from '@/types'

/// Compose a store's display address from its parts, skipping any that are
/// missing. Format: street, city, state zip — no country.
/// e.g. "34 Falcon St, Denver, CO 80202"
export function formatShopAddress(loc: LocationDetails | undefined | null): string {
  if (!loc) return ''
  const stateZip = [loc.state, loc.zipCode].filter((p) => p && String(p).trim()).join(' ')
  return [loc.address, loc.city, stateZip]
    .filter((p) => p && String(p).trim())
    .join(', ')
}

/// Live-mask a US phone number as "(555) 123-4567" while typing.
/// Values with more than 10 digits (not a plain US number) pass through
/// untouched so pre-existing data is never truncated.
export function formatUsPhoneInput(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (digits.length > 10) return value
  if (digits.length === 0) return ''
  if (digits.length <= 3) return `(${digits}`
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

/// Phone for display: US numbers (+1 with 10 digits) render as
/// "+1 (555) 123-4567"; anything else falls back to "code number".
export function formatPhoneDisplay(countryCode?: string | null, phone?: string | null): string {
  const code = (countryCode ?? '').trim()
  const raw = (phone ?? '').trim()
  if (!raw) return code
  if (code === '+1') {
    const digits = raw.replace(/\D/g, '')
    if (digits.length === 10) {
      return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    }
  }
  return [code, raw].filter(Boolean).join(' ')
}

/// Fallback currency when the selected store has none set.
export const DEFAULT_CURRENCY = 'USD'

/// The selected store's ISO‑4217 currency code, read synchronously (no hook).
/// Use as the default for money formatters that run outside React render.
export function getStoreCurrency(): string {
  return getSelectedStore()?.currency || DEFAULT_CURRENCY
}

/// The selected store's currency, re-rendering on store switch.
export function useStoreCurrency(): string {
  return React.useSyncExternalStore(
    subscribeSelectedStore,
    () => getSelectedStore()?.currency || DEFAULT_CURRENCY,
    () => DEFAULT_CURRENCY,
  )
}

/// Format integer cents as money in the store's currency. Pass an explicit
/// `currency` to override (defaults to the selected store's currency).
export const formatCurrency = (
  cents: number | null | undefined,
  currency: string = getStoreCurrency(),
): string => {
  const safeCents = typeof cents === 'number' ? cents : 0
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(safeCents / 100)
}
